/**
 * HandController - Wrapper sobre MediaPipe Tasks Vision
 * Gestiona el hand tracking y expone el estado suavizado
 */

import * as THREE from 'three';
import { 
  HandLandmarker, 
  FilesetResolver,
  HandLandmarkerResult 
} from '@mediapipe/tasks-vision';
import state, { HandState } from '../core/state';
import { HandFilterSet } from '../utils/OneEuroFilter';
import { HAND_TRACKING_CONFIG } from '../core/config';

export type CameraLayout = 'background' | 'pip';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class HandController {
  private handLandmarker: HandLandmarker | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private isRunning: boolean = false;

  // Canvas overlay para dibujar landmarks
  private overlayCanvas: HTMLCanvasElement | null = null;
  private overlayCtx: CanvasRenderingContext2D | null = null;

  // Cursor HTML virtual para interactuar con botones del DOM
  private domCursor: HTMLElement | null = null;
  private lastHoveredElement: HTMLElement | null = null;
  private wasPinchingForDOM = false;

  // Badge de estado: le dice al alumno en vivo si MediaPipe lo ve
  private statusBadge: HTMLElement | null = null;
  private lastDetectTs = 0;
  private frameCountSinceLog = 0;

  // Filtros para suavizado
  private handFilters: Map<'left' | 'right', HandFilterSet> = new Map();

  // Referencias previas para cálculo de velocidad
  private previousPositions: Map<'left' | 'right', THREE.Vector3> = new Map();

  // Callbacks
  onHandsDetected?: (hands: HandState[]) => void;
  onError?: (error: Error) => void;

  constructor() {
    // Inicializar filtros
    this.handFilters.set('left', new HandFilterSet({
      minCutoff: HAND_TRACKING_CONFIG.ONE_EURO_MIN_CUTOFF,
      beta: HAND_TRACKING_CONFIG.ONE_EURO_BETA,
      dCutoff: HAND_TRACKING_CONFIG.ONE_EURO_D_CUTOFF,
    }));
    this.handFilters.set('right', new HandFilterSet({
      minCutoff: HAND_TRACKING_CONFIG.ONE_EURO_MIN_CUTOFF,
      beta: HAND_TRACKING_CONFIG.ONE_EURO_BETA,
      dCutoff: HAND_TRACKING_CONFIG.ONE_EURO_D_CUTOFF,
    }));
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // INICIALIZACIÓN
  // ═════════════════════════════════════════════════════════════════════════════
  
  async initialize(): Promise<void> {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );

      this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: HAND_TRACKING_CONFIG.MODEL_PATH,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numHands: HAND_TRACKING_CONFIG.NUM_HANDS,
        minHandDetectionConfidence: HAND_TRACKING_CONFIG.MIN_DETECTION_CONFIDENCE,
        minHandPresenceConfidence: HAND_TRACKING_CONFIG.MIN_TRACKING_CONFIDENCE,
        minTrackingConfidence: HAND_TRACKING_CONFIG.MIN_TRACKING_CONFIDENCE,
      });

      console.log('[HandController] Initialized successfully');
    } catch (error) {
      console.error('[HandController] Failed to initialize:', error);
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Monta la cámara en uno de dos layouts:
   *   'background': video fullscreen detrás de la escena 3D (por defecto,
   *                 los nodos flotan sobre el alumno estilo AR).
   *   'pip':        preview pequeño en esquina inferior derecha.
   *
   * En ambos casos los landmarks de la mano se dibujan encima del video.
   * El canvas Three.js tiene alpha:true, así que el video se ve a través.
   */
  private currentLayout: CameraLayout = 'background';

  async startCamera(
    videoElement?: HTMLVideoElement,
    layout: CameraLayout = 'background'
  ): Promise<void> {
    this.currentLayout = layout;
    if (!this.handLandmarker) {
      throw new Error('HandController not initialized. Call initialize() first.');
    }

    this.videoElement = videoElement || document.createElement('video');
    this.videoElement.autoplay = true;
    this.videoElement.playsInline = true;
    this.videoElement.muted = true;

    if (!videoElement) {
      if (layout === 'background') {
        this.mountBackgroundCamera(this.videoElement);
      } else {
        this.mountPipCamera(this.videoElement);
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
      });

      this.videoElement.srcObject = stream;
      await this.videoElement.play();

      // Canvas overlay de landmarks: fullscreen si es background (los puntos
      // se ven sobre toda la pantalla), contenido en el PiP si es pip.
      this.overlayCanvas = document.createElement('canvas');
      this.overlayCanvas.width = 1280;
      this.overlayCanvas.height = 720;
      if (layout === 'background') {
        this.overlayCanvas.style.cssText = `
          position: fixed; inset: 0;
          width: 100vw; height: 100vh;
          pointer-events: none;
          transform: scaleX(-1);
          z-index: 500;
        `;
        document.body.appendChild(this.overlayCanvas);
      } else {
        this.overlayCanvas.style.cssText = `
          position: absolute; top: 0; left: 0;
          width: 100%; height: 100%;
          pointer-events: none;
          transform: scaleX(-1);
          border-radius: 12px;
        `;
        document.getElementById('cgt-camera-pip')?.appendChild(this.overlayCanvas);
      }
      this.overlayCtx = this.overlayCanvas.getContext('2d');

      this.createDomCursor();
      this.createStatusBadge();

      this.isRunning = true;
      this.detectLoop();

      console.log('[HandController] Camera started (layout:', layout, ')');
    } catch (error) {
      console.error('[HandController] Failed to start camera:', error);
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Video fullscreen detrás de la escena 3D. Desaturado + atenuado para no
   * competir con los nodos. El canvas Three.js (alpha:true) pasa el video
   * debajo; el HUD/sidebar siguen arriba gracias a su z-index elevado.
   */
  private mountBackgroundCamera(video: HTMLVideoElement): void {
    video.id = 'cgt-camera-bg';
    video.style.cssText = `
      position: fixed; inset: 0;
      width: 100vw; height: 100vh;
      object-fit: cover;
      transform: scaleX(-1);
      z-index: 0;
      opacity: 0.32;
      filter: saturate(0.4) contrast(1.05) brightness(0.85);
      pointer-events: none;
    `;
    // El body mantiene fondo negro (mezcla con opacity 0.32 = video atenuado).
    // #app a z-index:1 para que el canvas 3D quede encima del video.
    document.body.classList.add('cgt-camera-active');
    if (!document.getElementById('cgt-camera-bg-styles')) {
      const s = document.createElement('style');
      s.id = 'cgt-camera-bg-styles';
      s.textContent = `
        body.cgt-camera-active #app { z-index: 1; }
      `;
      document.head.appendChild(s);
    }
    document.body.appendChild(video);

    // Badge "EN VIVO" arriba derecha (debajo de sidebar/HUD, encima de overlay).
    const live = document.createElement('div');
    live.id = 'cgt-camera-live-badge';
    live.innerHTML = '<span></span>EN VIVO';
    // Esquina inferior izquierda: no compite con HUD, ClockHUD ni sidebar.
    live.style.cssText = `
      position: fixed; bottom: 14px; left: 14px;
      z-index: 9500;
      padding: 5px 12px; border-radius: 999px;
      background: rgba(10,10,10,0.75); backdrop-filter: blur(12px);
      border: 0.5px solid rgba(255,69,58,0.4);
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      font-size: 10px; font-weight: 700; letter-spacing: 1.2px;
      color: #FF6961; display: flex; align-items: center; gap: 7px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.35);
    `;
    const dot = live.querySelector('span') as HTMLElement;
    dot.style.cssText = `
      width: 6px; height: 6px; border-radius: 50%; background: #FF453A;
      animation: cgt-pulse 1.8s infinite;
    `;
    if (!document.getElementById('cgt-camera-keyframes')) {
      const s = document.createElement('style');
      s.id = 'cgt-camera-keyframes';
      s.textContent = `@keyframes cgt-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`;
      document.head.appendChild(s);
    }
    document.body.appendChild(live);
  }

  private mountPipCamera(video: HTMLVideoElement): void {
    video.style.cssText = `
      width: 100%; height: 100%;
      object-fit: cover;
      transform: scaleX(-1);
      border-radius: 12px;
      display: block;
    `;
    const pip = document.createElement('div');
    pip.id = 'cgt-camera-pip';
    pip.appendChild(video);
    const live = document.createElement('span');
    live.className = 'cgt-camera-live';
    live.textContent = 'CÁMARA';
    pip.appendChild(live);
    document.body.appendChild(pip);
  }

  /**
   * Cursor DOM grande + halo exterior. El halo anima cuando no hay hover:
   * así el alumno siempre ve dónde apunta su mano aunque no esté sobre un
   * elemento clickeable.
   */
  private createDomCursor(): void {
    this.domCursor = document.createElement('div');
    this.domCursor.id = 'hand-dom-cursor';
    this.domCursor.innerHTML = '<span class="cgt-cursor-halo"></span><span class="cgt-cursor-dot"></span>';
    this.domCursor.style.cssText = `
      position: fixed; width: 48px; height: 48px;
      pointer-events: none; z-index: 9999;
      transform: translate(-50%, -50%);
      transition: transform 0.08s, opacity 0.2s;
      display: none;
    `;

    if (!document.getElementById('cgt-cursor-styles')) {
      const s = document.createElement('style');
      s.id = 'cgt-cursor-styles';
      s.textContent = `
        #hand-dom-cursor .cgt-cursor-halo {
          position: absolute; inset: 0;
          border-radius: 50%;
          border: 2px solid rgba(41,151,255,0.55);
          background: radial-gradient(circle, rgba(41,151,255,0.18) 0%, rgba(41,151,255,0) 70%);
          box-shadow: 0 0 24px rgba(41,151,255,0.45);
          animation: cgt-cursor-pulse 1.4s ease-in-out infinite;
        }
        #hand-dom-cursor .cgt-cursor-dot {
          position: absolute; inset: 18px;
          border-radius: 50%; background: rgba(41,151,255,0.95);
          box-shadow: 0 0 8px rgba(41,151,255,0.8);
        }
        #hand-dom-cursor.cgt-cursor-hover .cgt-cursor-halo {
          border-color: rgba(48,209,88,0.8);
          background: radial-gradient(circle, rgba(48,209,88,0.25) 0%, rgba(48,209,88,0) 70%);
          box-shadow: 0 0 32px rgba(48,209,88,0.6);
          animation: none;
        }
        #hand-dom-cursor.cgt-cursor-hover .cgt-cursor-dot {
          background: rgba(48,209,88,1);
          box-shadow: 0 0 10px rgba(48,209,88,0.9);
        }
        #hand-dom-cursor.cgt-cursor-pinch .cgt-cursor-halo {
          border-color: rgba(255,159,10,1);
          background: radial-gradient(circle, rgba(255,159,10,0.35) 0%, rgba(255,159,10,0) 70%);
          box-shadow: 0 0 28px rgba(255,159,10,0.7);
          animation: none;
        }
        #hand-dom-cursor.cgt-cursor-pinch .cgt-cursor-dot {
          inset: 20px; background: rgba(255,159,10,1);
        }
        @keyframes cgt-cursor-pulse {
          0%, 100% { transform: scale(0.9); opacity: 0.7; }
          50% { transform: scale(1.05); opacity: 1; }
        }
      `;
      document.head.appendChild(s);
    }

    document.body.appendChild(this.domCursor);
  }

  /**
   * Badge visible con el estado del hand tracking. Le da al alumno feedback
   * inmediato: ¿me está viendo MediaPipe? ¿cuántas manos detecta? ¿estoy
   * haciendo pinch? Sin esto el usuario no sabe si tiene que levantar más
   * las manos, acercarse a la cámara, o si hay un bug.
   */
  private createStatusBadge(): void {
    this.statusBadge = document.createElement('div');
    this.statusBadge.id = 'cgt-hand-status';
    this.statusBadge.style.cssText = `
      position: fixed; top: 14px; left: 50%; transform: translateX(-50%);
      z-index: 9400;
      padding: 7px 14px; border-radius: 999px;
      background: rgba(10,10,10,0.82); backdrop-filter: blur(12px);
      border: 0.5px solid rgba(255,255,255,0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
      color: #a1a1a6;
      display: flex; align-items: center; gap: 8px;
      transition: color 0.25s, border-color 0.25s, background 0.25s;
      pointer-events: none;
    `;
    this.statusBadge.innerHTML = `
      <span data-status-dot style="width:7px;height:7px;border-radius:50%;background:#FF9F0A;box-shadow:0 0 6px rgba(255,159,10,0.6);"></span>
      <span data-status-text>Buscando manos…</span>
    `;
    document.body.appendChild(this.statusBadge);
  }

  private setStatus(text: string, color: 'neutral' | 'green' | 'orange' | 'red'): void {
    if (!this.statusBadge) return;
    const dot = this.statusBadge.querySelector<HTMLElement>('[data-status-dot]');
    const txt = this.statusBadge.querySelector<HTMLElement>('[data-status-text]');
    if (txt) txt.textContent = text;
    if (dot) {
      const palette: Record<typeof color, string> = {
        neutral: '#8e8e93',
        green: '#30D158',
        orange: '#FF9F0A',
        red: '#FF453A',
      };
      const c = palette[color];
      dot.style.background = c;
      dot.style.boxShadow = `0 0 6px ${c}99`;
    }
    this.statusBadge.style.color = color === 'green' ? '#30D158' : '#a1a1a6';
  }

  /**
   * Cambia el modo de cámara en runtime.
   *   'background' / 'pip': montar cámara en ese layout
   *   'off':                detener cámara por completo
   * Seguro para invocar repetidamente; desmonta el anterior antes de montar.
   */
  async setCameraMode(mode: CameraLayout | 'off'): Promise<void> {
    this.stop();
    if (mode === 'off') return;
    try {
      await this.startCamera(undefined, mode);
    } catch (e) {
      console.warn('[HandController] setCameraMode failed:', e);
    }
  }

  getCameraLayout(): CameraLayout {
    return this.currentLayout;
  }

  isCameraOn(): boolean {
    return this.isRunning;
  }

  stop(): void {
    this.isRunning = false;

    if (this.videoElement?.srcObject) {
      const tracks = (this.videoElement.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
    }

    // Limpiar overlay, cursor DOM, video, badges
    this.overlayCanvas?.remove();
    this.overlayCanvas = null;
    this.overlayCtx = null;
    this.domCursor?.remove();
    this.domCursor = null;
    this.statusBadge?.remove();
    this.statusBadge = null;
    document.getElementById('cgt-camera-bg')?.remove();
    document.getElementById('cgt-camera-pip')?.remove();
    document.getElementById('cgt-camera-live-badge')?.remove();
    document.body.classList.remove('cgt-camera-active');

    // Resetear estado
    state.updateHandState('left', { isPresent: false });
    state.updateHandState('right', { isPresent: false });

    console.log('[HandController] Stopped');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LOOP DE DETECCIÓN
  // ═════════════════════════════════════════════════════════════════════════════
  
  private detectLoop(): void {
    if (!this.isRunning || !this.handLandmarker || !this.videoElement) return;

    // Evitar llamar al modelo antes de que el video tenga frames: detectForVideo
    // falla silenciosa si readyState < 2 y deja al usuario sin feedback.
    if (this.videoElement.readyState < 2) {
      this.setStatus('Esperando video…', 'orange');
      requestAnimationFrame(() => this.detectLoop());
      return;
    }

    try {
      const results = this.handLandmarker.detectForVideo(
        this.videoElement,
        performance.now()
      );
      this.processResults(results);
    } catch (e) {
      console.warn('[HandController] Detection error:', e);
      this.setStatus('Error en detección', 'red');
    }

    requestAnimationFrame(() => this.detectLoop());
  }

  private processResults(results: HandLandmarkerResult): void {
    const timestamp = performance.now();
    const detectedHands: HandState[] = [];

    // Limpiar overlay
    if (this.overlayCtx && this.overlayCanvas) {
      this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    }

    const nHands = results.landmarks?.length ?? 0;
    let anyPinching = false;

    if (nHands > 0) {
      results.landmarks!.forEach((landmarks, index) => {
        const handedness = results.handedness?.[index]?.[0]?.categoryName?.toLowerCase() || 'right';
        // Invertir handedness: cámara frontal está espejada, MediaPipe reporta desde perspectiva de la cámara
        const handKey = (handedness === 'left' ? 'right' : 'left') as 'left' | 'right';

        // Dibujar landmarks en el overlay
        this.drawHandLandmarks(landmarks, handKey);

        const handState = this.processHand(landmarks, handKey, timestamp);
        detectedHands.push(handState);
        if (handState.isPinching) anyPinching = true;

        // Interacción DOM con la mano derecha (o la activa)
        if (handKey === 'right' || !state.rightHand.isPresent) {
          this.updateDOMInteraction(landmarks, handState.isPinching);
        }
      });
    }

    // Actualizar badge de estado: le da al alumno feedback inmediato.
    if (nHands === 0) {
      // Log único cuando se pierde la detección para no inundar consola
      if (timestamp - this.lastDetectTs > 1500 && this.lastDetectTs > 0) {
        console.info('[HandController] Sin manos detectadas. Levantá las manos frente a la cámara.');
        this.lastDetectTs = 0;
      }
      this.setStatus('Levantá las manos frente a la cámara', 'orange');
    } else {
      if (timestamp - this.lastDetectTs > 2000) {
        console.info(`[HandController] ✋ ${nHands} mano(s) detectada(s)`);
      }
      this.lastDetectTs = timestamp;
      const pinchTxt = anyPinching ? ' · 👌 pinch!' : '';
      this.setStatus(`✋ ${nHands} mano${nHands > 1 ? 's' : ''} detectada${nHands > 1 ? 's' : ''}${pinchTxt}`, anyPinching ? 'green' : 'green');
    }

    // Log cada 120 frames (~2s) si está corriendo, para saber que el loop vive
    this.frameCountSinceLog++;
    if (this.frameCountSinceLog >= 120) {
      this.frameCountSinceLog = 0;
      console.debug(`[HandController] detect loop alive, hands=${nHands}`);
    }

    // Ocultar cursor DOM si no hay manos
    if (detectedHands.length === 0 && this.domCursor) {
      this.domCursor.style.display = 'none';
      if (this.lastHoveredElement) {
        this.lastHoveredElement.style.outline = '';
        this.lastHoveredElement = null;
      }
    }

    // Actualizar estado para manos no detectadas
    const detectedKeys = new Set(detectedHands.map(h => h === state.leftHand ? 'left' : 'right'));
    if (!detectedKeys.has('left')) {
      state.updateHandState('left', { isPresent: false });
    }
    if (!detectedKeys.has('right')) {
      state.updateHandState('right', { isPresent: false });
    }

    this.onHandsDetected?.(detectedHands);
  }

  private processHand(
    landmarks: Array<{x: number, y: number, z: number}>,
    handKey: 'left' | 'right',
    timestamp: number
  ): HandState {
    const filterSet = this.handFilters.get(handKey)!;
    
    // Convertir landmarks a posiciones 3D y filtrar
    const filteredLandmarks: Array<{x: number, y: number, z: number}> = landmarks.map((lm, index) => {
      const position = new THREE.Vector3(lm.x, lm.y, lm.z);
      
      // Aplicar filtro One Euro
      filterSet.filterLandmarkInPlace(index, position, timestamp);
      
      // Flip coordenadas si es necesario
      if (HAND_TRACKING_CONFIG.FLIP_X) position.x = 1 - position.x;
      if (HAND_TRACKING_CONFIG.FLIP_Y) position.y = 1 - position.y;
      
      return {x: position.x, y: position.y, z: position.z};
    });

    // Calcular posición promedio de la mano (basada en wrist y MCPs)
    const wrist = filteredLandmarks[0];
    const mcpJoints = [5, 9, 13, 17].map(i => filteredLandmarks[i]);
    
    let avgX = wrist.x;
    let avgY = wrist.y;
    let avgZ = wrist.z;
    
    mcpJoints.forEach(joint => {
      avgX += joint.x;
      avgY += joint.y;
      avgZ += joint.z;
    });
    
    avgX /= (mcpJoints.length + 1);
    avgY /= (mcpJoints.length + 1);
    avgZ /= (mcpJoints.length + 1);

    // Mapear a espacio 3D del mundo médico
    const handPosition = new THREE.Vector3(
      (avgX - 0.5) * HAND_TRACKING_CONFIG.HAND_SCALE + HAND_TRACKING_CONFIG.HAND_OFFSET_X,
      (0.5 - avgY) * HAND_TRACKING_CONFIG.HAND_SCALE + HAND_TRACKING_CONFIG.HAND_OFFSET_Y,
      avgZ * HAND_TRACKING_CONFIG.HAND_SCALE + HAND_TRACKING_CONFIG.HAND_OFFSET_Z
    );

    // Calcular velocidad
    const previousPos = this.previousPositions.get(handKey);
    const velocity = previousPos 
      ? handPosition.clone().sub(previousPos).divideScalar(0.016) // asumiendo 60fps
      : new THREE.Vector3(0, 0, 0);
    
    this.previousPositions.set(handKey, handPosition.clone());

    // Calcular pinch distance
    const thumbTip = filteredLandmarks[4];
    const indexTip = filteredLandmarks[8];
    const pinchDistance = Math.sqrt(
      Math.pow(thumbTip.x - indexTip.x, 2) +
      Math.pow(thumbTip.y - indexTip.y, 2) +
      Math.pow(thumbTip.z - indexTip.z, 2)
    );

    // Determinar estado
    const isPinching = pinchDistance < HAND_TRACKING_CONFIG.PINCH_THRESHOLD;
    
    // Calcular si está haciendo grab (puño cerrado)
    const grabStrength = this.calculateGrabStrength(filteredLandmarks);
    const isGrabbing = grabStrength > 0.5;

    // Posición 2D del índice ya flipeada (cámara frontal = espejo). Es el
    // input "fiable" para hit-test contra nodos 3D: se usa para raycast
    // como si fuera el mouse, evitando el Z poco confiable de MediaPipe.
    const rawIndex = landmarks[8];
    const indexTipScreen = rawIndex
      ? { x: 1 - rawIndex.x, y: rawIndex.y }
      : null;

    const handState: HandState = {
      isPresent: true,
      isPinching,
      isGrabbing,
      pinchDistance,
      position: handPosition,
      velocity,
      landmarks: filteredLandmarks,
      indexTipScreen,
      gesture: null,
      lastGestureTime: 0,
    };

    // Actualizar estado global
    state.updateHandState(handKey, handState);

    return handState;
  }

  private calculateGrabStrength(landmarks: Array<{x: number, y: number, z: number}>): number {
    const wrist = landmarks[0];
    const tips = [4, 8, 12, 16, 20].map(i => landmarks[i]);
    
    let totalDistance = 0;
    tips.forEach(tip => {
      totalDistance += Math.sqrt(
        Math.pow(tip.x - wrist.x, 2) +
        Math.pow(tip.y - wrist.y, 2) +
        Math.pow(tip.z - wrist.z, 2)
      );
    });
    
    const avgDistance = totalDistance / tips.length;
    return Math.max(0, Math.min(1, 1 - avgDistance * 2));
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // DIBUJO DE LANDMARKS 2D
  // ═════════════════════════════════════════════════════════════════════════════

  private drawHandLandmarks(
    landmarks: Array<{x: number, y: number, z: number}>,
    handKey: 'left' | 'right'
  ): void {
    if (!this.overlayCtx || !this.overlayCanvas) return;

    const ctx = this.overlayCtx;
    const w = this.overlayCanvas.width;
    const h = this.overlayCanvas.height;
    const color = handKey === 'right' ? '#3B82F6' : '#10B981';

    // Conexiones entre landmarks (esqueleto de la mano)
    const connections = [
      [0,1],[1,2],[2,3],[3,4],       // Pulgar
      [0,5],[5,6],[6,7],[7,8],       // Índice
      [5,9],[9,10],[10,11],[11,12],   // Medio
      [9,13],[13,14],[14,15],[15,16], // Anular
      [13,17],[17,18],[18,19],[19,20],// Meñique
      [0,17],                         // Palma
    ];

    // Conexiones con glow sutil para visibilidad sobre video
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.globalAlpha = 0.9;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    connections.forEach(([a, b]) => {
      const la = landmarks[a];
      const lb = landmarks[b];
      ctx.beginPath();
      ctx.moveTo(la.x * w, la.y * h);
      ctx.lineTo(lb.x * w, lb.y * h);
      ctx.stroke();
    });
    ctx.shadowBlur = 0;

    // Puntos: tips (yemas) grandes y blancos, resto color
    ctx.globalAlpha = 1;
    landmarks.forEach((lm, i) => {
      const x = lm.x * w;
      const y = lm.y * h;
      const isTip = [4, 8, 12, 16, 20].includes(i);
      const radius = isTip ? 9 : 5;

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = isTip ? '#ffffff' : color;
      ctx.fill();

      if (isTip) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    });

    // Indicador de pinch (línea entre pulgar e índice)
    const thumb = landmarks[4];
    const index = landmarks[8];
    const pinchDist = Math.sqrt(
      Math.pow(thumb.x - index.x, 2) + Math.pow(thumb.y - index.y, 2)
    );
    if (pinchDist < 0.08) {
      ctx.beginPath();
      ctx.arc(
        (thumb.x + index.x) / 2 * w,
        (thumb.y + index.y) / 2 * h,
        22, 0, Math.PI * 2
      );
      ctx.strokeStyle = '#F59E0B';
      ctx.lineWidth = 4;
      ctx.globalAlpha = 1;
      ctx.shadowColor = '#F59E0B';
      ctx.shadowBlur = 14;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    ctx.globalAlpha = 1;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // INTERACCIÓN CON PANTALLA (DOM + Canvas 3D)
  // ═════════════════════════════════════════════════════════════════════════════

  private updateDOMInteraction(
    landmarks: Array<{x: number, y: number, z: number}>,
    isPinching: boolean
  ): void {
    if (!this.domCursor) return;

    const indexTip = landmarks[8];
    if (!indexTip) return;

    // Posición en pantalla — espejado porque cámara frontal
    const screenX = (1 - indexTip.x) * window.innerWidth;
    const screenY = indexTip.y * window.innerHeight;

    // Mover cursor
    this.domCursor.style.display = 'block';
    this.domCursor.style.left = `${screenX}px`;
    this.domCursor.style.top = `${screenY}px`;

    // Buscar elemento bajo el cursor
    this.domCursor.style.pointerEvents = 'none';
    this.domCursor.style.display = 'none';
    const el = document.elementFromPoint(screenX, screenY) as HTMLElement | null;
    this.domCursor.style.display = 'block';

    const clickable = el?.closest('button, [onclick], a, .quiz-option, .eop-exam, .diff-confirm-btn, .tl-marker, .tl-triage-btn, canvas') as HTMLElement | null;

    // Hover feedback — clases CSS (verde cuando hay target clickeable)
    if (clickable && clickable !== this.lastHoveredElement) {
      if (this.lastHoveredElement && this.lastHoveredElement.tagName !== 'CANVAS') {
        this.lastHoveredElement.style.outline = '';
      }
      this.lastHoveredElement = clickable;
      if (clickable.tagName !== 'CANVAS') {
        clickable.style.outline = '2px solid rgba(48, 209, 88, 0.6)';
        clickable.style.outlineOffset = '2px';
      }
      this.domCursor.classList.add('cgt-cursor-hover');
    } else if (!clickable && this.lastHoveredElement) {
      if (this.lastHoveredElement.tagName !== 'CANVAS') {
        this.lastHoveredElement.style.outline = '';
      }
      this.lastHoveredElement = null;
      this.domCursor.classList.remove('cgt-cursor-hover');
    }

    const eventInit: MouseEventInit = {
      clientX: screenX,
      clientY: screenY,
      bubbles: true,
      cancelable: true,
      view: window,
    };

    // Siempre enviar mousemove para hover/raycasting
    if (el) {
      el.dispatchEvent(new MouseEvent('mousemove', eventInit));
    }

    // ── PINCH START ──
    if (isPinching && !this.wasPinchingForDOM) {
      this.domCursor.classList.add('cgt-cursor-pinch');

      if (el) {
        el.dispatchEvent(new MouseEvent('mousedown', { ...eventInit, button: 0 }));
      }
      if (clickable && clickable.tagName !== 'CANVAS') {
        clickable.click();
        console.log('[Hand] Click:', clickable.textContent?.trim().substring(0, 30));
      }
    }

    // ── PINCH SOSTENIDO ──
    if (isPinching) {
      if (el) {
        el.dispatchEvent(new MouseEvent('mousemove', eventInit));
      }
    }

    // ── PINCH END ──
    if (!isPinching && this.wasPinchingForDOM) {
      window.dispatchEvent(new MouseEvent('mouseup', eventInit));
      this.domCursor.classList.remove('cgt-cursor-pinch');
    }

    this.wasPinchingForDOM = isPinching;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════

  getHandPosition(hand: 'left' | 'right'): THREE.Vector3 | null {
    const handState = hand === 'left' ? state.leftHand : state.rightHand;
    return handState.isPresent ? handState.position.clone() : null;
  }

  getBothHandPositions(): [THREE.Vector3 | null, THREE.Vector3 | null] {
    return [
      this.getHandPosition('left'),
      this.getHandPosition('right'),
    ];
  }

  isHandPresent(hand?: 'left' | 'right'): boolean {
    if (hand) {
      return hand === 'left' ? state.leftHand.isPresent : state.rightHand.isPresent;
    }
    return state.leftHand.isPresent || state.rightHand.isPresent;
  }

  resetFilters(): void {
    this.handFilters.forEach(filter => filter.reset());
    this.previousPositions.clear();
  }

  setFilterParams(params: { minCutoff?: number; beta?: number; dCutoff?: number }): void {
    this.handFilters.forEach(filter => filter.setParameters(params));
  }
}

export default HandController;
